"use client";

import { useState, useEffect, useRef, forwardRef } from "react";
import type { CSSProperties } from "react";
import { motion } from "motion/react";
import { AccentStar } from "@/shared/ui/accent-star";
import { SectionBlock } from "@/components/blocks/SectionBlock";
import { useScrollReveal, type RevealState } from "@/shared/lib/useScrollReveal";

/* ─────────────────────────────────────────────────────────────
   Breathing data
───────────────────────────────────────────────────────────── */
const PHASE_COLORS = {
  inhale: "var(--school)",
  hold: "var(--help)",
  exhale: "var(--media)",
  pause: "var(--muted)",
} as const;

const PHASE_DURATIONS = {
  inhale: 4,
  hold: 4,
  exhale: 4,
  pause: 4,
} as const;

const PHASE_LABELS = {
  inhale: "Вдох",
  hold: "Задержка",
  exhale: "Выдох",
  pause: "Задержка",
} as const;

const PHASE_ORDER: Array<"inhale" | "hold" | "exhale" | "pause"> = [
  "inhale",
  "hold",
  "exhale",
  "pause",
];

/* ─────────────────────────────────────────────────────────────
   Стиль-хелперы
───────────────────────────────────────────────────────────── */
function fromBottom(state: RevealState, delay: number): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "translateY(2.5rem)" };
  return {
    opacity: 0,
    animation: `slide-from-bottom 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay.toFixed(2)}s forwards`,
  };
}

function fadeScaleIn(state: RevealState, delay: number): CSSProperties {
  if (state === "done") return {};
  if (state === "idle") return { opacity: 0, transform: "scale(0.95)" };
  return {
    opacity: 0,
    animation: `fade-scale-in 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay.toFixed(2)}s forwards`,
  };
}

/* ─────────────────────────────────────────────────────────────
   Компонент
───────────────────────────────────────────────────────────── */
export const AntiStressSection = forwardRef<HTMLElement>((_, ref) => {
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState<"inhale" | "hold" | "exhale" | "pause">(
    "inhale",
  );
  const [secondsLeft, setSecondsLeft] = useState<number>(
    PHASE_DURATIONS.inhale,
  );
  const [fillPct, setFillPct] = useState<number>(0);
  const rafRef = useRef(0);
  const startRef = useRef(0);
  const iterationRef = useRef(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const { ref: revealRef, state } = useScrollReveal("antistress", 0.1);

  const setRefs = (el: HTMLElement | null) => {
    revealRef(el);
    if (typeof ref === "function") ref(el);
    else if (ref)
      (ref as React.MutableRefObject<HTMLElement | null>).current = el;
  };

  useEffect(() => {
    if (!active) return;
    startRef.current = performance.now();

    const tick = () => {
      const elapsed = (performance.now() - startRef.current) / 1000;
      const dur = PHASE_DURATIONS[phaseRef.current];
      const t = Math.min(elapsed / dur, 1);

      let fill: number;
      if (phaseRef.current === "inhale") fill = t * 100;
      else if (phaseRef.current === "hold") fill = 100;
      else if (phaseRef.current === "exhale") fill = (1 - t) * 100;
      else fill = 0;

      setFillPct(fill);
      setSecondsLeft(Math.max(0, Math.ceil(dur - elapsed)));

      if (t >= 1) {
        const idx = PHASE_ORDER.indexOf(phaseRef.current);
        const next = PHASE_ORDER[(idx + 1) % 4];

        if (next === "inhale") {
          iterationRef.current += 1;
          if (iterationRef.current >= 10) {
            setActive(false);
            setPhase("inhale");
            phaseRef.current = "inhale";
            setFillPct(0);
            setSecondsLeft(PHASE_DURATIONS.inhale);
            return;
          }
        }

        phaseRef.current = next;
        setPhase(next);
        setSecondsLeft(PHASE_DURATIONS[next]);
        startRef.current = performance.now();
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active]);

  const handleStart = () => {
    setPhase("inhale");
    phaseRef.current = "inhale";
    setSecondsLeft(PHASE_DURATIONS.inhale);
    setFillPct(0);
    iterationRef.current = 0;
    setActive(true);
  };

  const handleStop = () => {
    setActive(false);
    setPhase("inhale");
    phaseRef.current = "inhale";
    setFillPct(0);
    setSecondsLeft(PHASE_DURATIONS.inhale);
  };

  const phaseColor = PHASE_COLORS[phase];

  return (
    <section
      ref={setRefs}
      className="relative w-full h-auto flex flex-col"
    >
      <SectionBlock className="flex flex-col gap-fluid-2xl p-fluid-xl">

        {/* Заголовок: одним блоком сверху вниз (issue 1), DOM: p → h2 */}
        <div className="overflow-hidden">
          <div
            style={
              state === "done" ? {} :
              state === "idle" ? { opacity: 0, transform: "translateY(-2.5rem)" } :
              { opacity: 0, animation: "slide-from-top 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s forwards" }
            }
            className="text-left"
          >
            <h2 className="font-content font-semibold text-fluid-4xl leading-tight text-foreground block select-none">
              Техника дыхания <span className="text-help">по квадрату</span>
            </h2>
            <p className="font-content font-light text-fluid-xl leading-relaxed text-foreground/80 block select-none mt-fluid-sm max-w-[65ch]">
              Попробуйте технику из ДБТ «Дыхание по квадрату». Она помогает справиться с сильными эмоциями.
            </p>
          </div>
        </div>

        {/* Инструкция по технике — 4 шага + повтор */}
        <div className="pt-fluid-lg border-t border-[var(--help)]/20 flex flex-col gap-fluid-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-fluid-lg">
            {[
              { num: "1", label: "Вдох", text: "Сделайте вдох на 4 счета." },
              { num: "2", label: "Задержка", text: "Задержите дыхание на 4 счета." },
              { num: "3", label: "Выдох", text: "Сделайте выдох на 4 счета." },
              { num: "4", label: "Задержка", text: "Задержите дыхание на 4 счета." },
            ].map((card, i) => (
              <div
                key={card.num}
                style={fromBottom(state, 0.2 + i * 0.15)}
                className="flex flex-col gap-fluid-xs"
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-[var(--help)] font-bold opacity-40 text-fluid-sm">{card.num}.</span>
                  <span className="font-heading text-fluid-base text-help">{card.label}</span>
                </div>
                <p className="font-content text-fluid-sm text-foreground/70 leading-relaxed">
                  {card.text}
                </p>
              </div>
            ))}
          </div>
          {/* Callout повтора */}
          <div
            style={fromBottom(state, 0.2 + 4 * 0.15)}
            className="rounded-xl border border-[var(--help)]/20 px-fluid-lg py-fluid-md"
          >
            <p className="font-content text-fluid-sm text-foreground/70 leading-relaxed font-medium italic">
              Повторите цикл 10 раз или столько, сколько потребуется для снижения эмоционального накала.
            </p>
          </div>
        </div>

        {/* Виджет дыхания — fade-scale-in после карточек */}
        <div
          style={fadeScaleIn(state, 0.65)}
          className="pt-fluid-lg border-t border-[var(--help)]/20 flex flex-col gap-fluid-xl"
        >
          <div className="flex-1 flex flex-col items-center justify-center min-h-[clamp(300px,55svh,700px)] relative z-10 gap-fluid-md @container/area [container-type:size]">
            <div className="flex-1 flex items-center justify-center w-full">
              <motion.div
                className="relative flex items-center justify-center cursor-pointer @container/circle inline-size pointer-events-auto"
                animate={{
                  width: active
                    ? "clamp(16rem, min(80cqw, 80cqh), 40rem)"
                    : "clamp(12rem, min(40cqw, 40cqh), 20rem)",
                  height: active
                    ? "clamp(16rem, min(80cqw, 80cqh), 40rem)"
                    : "clamp(12rem, min(40cqw, 40cqh), 20rem)",
                  y: active ? "-5cqh" : 0,
                }}
                transition={{
                  duration: active ? 1.2 : 0.6,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                onClick={active ? handleStop : handleStart}
              >
                <div
                  className="absolute inset-0 rounded-full transition-colors duration-700"
                  style={{
                    border: `2px solid ${active ? phaseColor : "var(--border)"}`,
                  }}
                />

                <div
                  className="rounded-full overflow-hidden relative transition-colors duration-700 w-[65%] h-[65%]"
                  style={{
                    backgroundColor: active ? "var(--background)" : "var(--help)",
                    border: active ? `2px solid ${phaseColor}` : "none",
                  }}
                >
                  {active && (
                    <div
                      className="absolute bottom-0 left-0 right-0"
                      style={{
                        height: `${fillPct}%`,
                        backgroundColor: phaseColor,
                        transition: "background-color 0.7s ease",
                      }}
                    />
                  )}

                  {active ? (
                    <div className="absolute inset-0 z-10 pointer-events-none">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span
                          className="text-[15cqw] tabular-nums select-none font-heading"
                          style={{
                            color: phaseColor,
                            transition: "color 0.7s ease",
                          }}
                        >
                          {secondsLeft}
                        </span>
                      </div>
                      <div
                        className="absolute inset-0 flex items-center justify-center"
                        style={{
                          clipPath: `inset(${100 - fillPct}% 0 0 0)`,
                        }}
                      >
                        <span className="text-[15cqw] tabular-nums select-none text-background font-heading">
                          {secondsLeft}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                      <span className="text-fluid-base tracking-wide text-white font-content">
                        Начать
                      </span>
                    </div>
                  )}
                </div>

                {active && (
                  <>
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="absolute"
                        animate={{ rotate: [0, 360] }}
                        transition={{
                          rotate: {
                            duration: 16 + i * 4,
                            repeat: Infinity,
                            ease: "linear",
                          },
                        }}
                        style={{
                          top: `${15 + i * 28}%`,
                          left: i === 1 ? "88%" : i === 0 ? "5%" : "80%",
                        }}
                      >
                        <AccentStar color={phaseColor} size={8 + i * 3} />
                      </motion.div>
                    ))}
                  </>
                )}
              </motion.div>
            </div>

            <div className="flex items-center gap-fluid-md flex-shrink-0 flex-wrap justify-center">
              {PHASE_ORDER.map((p) => (
                <div key={p} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: PHASE_COLORS[p],
                      opacity: active && phase === p ? 1 : 0.3,
                      transform: active && phase === p ? "scale(1.3)" : "scale(1)",
                    }}
                  />
                  <span
                    className="text-fluid-xs transition-colors duration-300 font-content text-muted"
                    style={
                      active && phase === p ? { color: PHASE_COLORS[p] } : undefined
                    }
                  >
                    {PHASE_LABELS[p]}
                  </span>
                </div>
              ))}
            </div>

            {active && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-fluid-sm transition-colors flex-shrink-0 font-content text-muted lg:hover:text-help"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStop();
                }}
              >
                Остановить
              </motion.button>
            )}
          </div>
        </div>
      </SectionBlock>
    </section>
  );
});

AntiStressSection.displayName = "AntiStressSection";
